import { useEffect } from 'react';
import { useGcsStore } from '../../store/useGcsStore';
import { useNotificationStore } from '../../store/useNotificationStore';
import { UssConnectionState } from '../../types/gcs/uss';
import { DEFAULT_NOTIFICATION_DURATION_MS, InAppNotificationType } from '../../types/ui/inAppNotification';
import { en } from '../../locales/en';
import type { GcsStore } from '../../store/useGcsStore';
import { getServerNameCode } from '../../config/gcs/gcsConfigs';

export function UssConnectionStateListener() {
  useEffect(() => {
    const selectUssTransitions = (state: GcsStore): Map<string, UssConnectionState | null> => {
      const transitions = new Map<string, UssConnectionState | null>();
      state.data.forEach((gcsData, gcsId) => {
        transitions.set(gcsId, gcsData.ussConnectionState ?? null);
      });
      return transitions;
    };

    // Custom equality function to compare Map contents
    const equalityFn = (
      a: Map<string, UssConnectionState | null>,
      b: Map<string, UssConnectionState | null>
    ): boolean => {
      if (a.size !== b.size) return false;
      for (const [key, value] of a) {
        if (b.get(key) !== value) return false;
      }
      return true;
    };

    const unsubscribe = useGcsStore.subscribe(
      selectUssTransitions,
      (nextTransitions, prevTransitions = new Map()) => {
        const showNotification = useNotificationStore.getState().showNotification;

        nextTransitions.forEach((nextState, gcsId) => {
          const previousState = prevTransitions.get(gcsId);
          if (!previousState || previousState === nextState) return;
          const serverNameCode = getServerNameCode(gcsId);
          if (nextState === UssConnectionState.CONNECTED && previousState === UssConnectionState.DISCONNECTED) {
            showNotification({
              type: InAppNotificationType.SUCCESS,
              body: `${serverNameCode} - ${en.inAppNotifications.ussServerConnectionRegainedBody}`,
              durationMs: DEFAULT_NOTIFICATION_DURATION_MS
            });
          } else if (nextState === UssConnectionState.DISCONNECTED && previousState === UssConnectionState.CONNECTED) {
            showNotification({
              type: InAppNotificationType.ERROR,
              body: `${serverNameCode} - ${en.inAppNotifications.ussServerConnectionLostBody}`,
              durationMs: DEFAULT_NOTIFICATION_DURATION_MS
            });
          }
        });
      },
      {
        fireImmediately: true,
        equalityFn
      }
    );

    return unsubscribe;
  }, []);

  return null;
}
